import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';

import { Label } from '@src/design-system/label';
import { Switch } from '@src/design-system/switch';
import { FormikDatePicker } from '@src/shared/fields/formik-date-picker-field';
import { Textfield } from '@src/shared/fields/text-field';
import { TimezoneField } from '@src/shared/fields/timezone-field';

import { getCurrentScheduleFields } from '../../utils/get-current-schedule-fields';
import type { TCreateReminderFormValues } from './schema';

type CreateReminderScheduleSectionProps = {
  embedded?: boolean;
};

export function CreateReminderScheduleSection({
  embedded = false,
}: CreateReminderScheduleSectionProps = {}) {
  const { values, setFieldValue } =
    useFormikContext<TCreateReminderFormValues>();

  useEffect(() => {
    if (!embedded) return;

    setFieldValue('scheduleDelivery', true, false);

    if (!values.scheduledDate && !values.scheduledTime && !values.timezone) {
      const { scheduledDate, scheduledTime, timezone } =
        getCurrentScheduleFields();
      setFieldValue('scheduledDate', scheduledDate, false);
      setFieldValue('scheduledTime', scheduledTime, false);
      setFieldValue('timezone', timezone, false);
    }
  }, [
    embedded,
    setFieldValue,
    values.scheduledDate,
    values.scheduledTime,
    values.timezone,
  ]);

  const handleScheduleDeliveryChange = (checked: boolean) => {
    setFieldValue('scheduleDelivery', checked);

    if (!checked) {
      const { scheduledDate, scheduledTime, timezone } =
        getCurrentScheduleFields();
      setFieldValue('scheduledDate', scheduledDate);
      setFieldValue('scheduledTime', scheduledTime);
      setFieldValue('timezone', timezone);
      return;
    }

    if (!values.scheduledDate && !values.scheduledTime && !values.timezone) {
      const { scheduledDate, scheduledTime, timezone } =
        getCurrentScheduleFields();
      setFieldValue('scheduledDate', scheduledDate);
      setFieldValue('scheduledTime', scheduledTime);
      setFieldValue('timezone', timezone);
    }
  };

  const scheduleDateTimeFields = (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <FormikDatePicker
          name='scheduledDate'
          label='Date'
          minValue={today(getLocalTimeZone())}
        />
        <Textfield
          name='scheduledTime'
          label='Time (User Locale)'
          type='time'
        />
      </div>
      <div>
        <Label className='mb-2 inline-block'>Time Zone</Label>
        <TimezoneField
          selectName='timezone'
          selectPlaceholder='Select time zone'
        />
      </div>
    </div>
  );

  const scheduleFields = embedded ? (
    scheduleDateTimeFields
  ) : (
    <>
      <div
        className='flex items-center gap-2'
        role='group'
        aria-label='Delivery timing'
      >
        <Switch
          checked={values.scheduleDelivery}
          onCheckedChange={handleScheduleDeliveryChange}
          aria-label={
            values.scheduleDelivery ? 'Schedule delivery' : 'Send now'
          }
          className='h-7 w-12 shrink-0 data-[state=checked]:bg-primary-1000 data-[state=unchecked]:bg-gray-200 [&>span]:size-5 [&>span]:data-[state=checked]:translate-x-5 [&>span]:data-[state=unchecked]:translate-x-0.5'
        />
        <span className='text-sm font-medium transition-colors text-gray-900'>
          Schedule Delivery
        </span>
      </div>

      {values.scheduleDelivery ? scheduleDateTimeFields : null}
    </>
  );

  if (embedded) {
    return <div className='flex flex-col gap-4'>{scheduleFields}</div>;
  }

  return (
    <section className='rounded-xl border border-[#E8E7F0] bg-white p-4'>
      <div className='mb-4 flex flex-col justify-between gap-5'>
        <p className='text-sm font-semibold text-gray-500'>
          3. Schedule Delivery
        </p>
        {scheduleFields}
      </div>
    </section>
  );
}
